import { T, useTranslate } from '@tolgee/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoIosClose } from 'react-icons/io';
import { useDebounce } from 'react-use';
import {
  FieldLabel,
  FormField,
  FormInputWithIcon,
  InputIcon,
  InputWrapper,
} from '~/app/components/ConflictOfInterest/Form/styles';
import { useInfinityGetEmployees } from '~/entities/Employee/api/useGetEmployees';
import { EmployeeDetailsDto, EmployeeInterface } from '~/entities/Employee/dto/employee-details.dto';
// eslint-disable-next-line import/no-cycle
import { SearchIcon } from '@ghq-abi/design-system-icons';
import { mapEmployeeDetails } from '~/entities/Employee/utils/mapEmployeeDetails';
import { DropdownContainer, LoadingOption, NoResultsOption, SelectOption } from './styles';

interface EmployeeSearchProps {
  onEmployeeSelect: (employee: EmployeeInterface) => void;
  onClear?: () => void;
}

export const EmployeeSearch: React.FC<EmployeeSearchProps> = ({
  onEmployeeSelect,
  onClear,
}) => {
  const { t } = useTranslate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useDebounce(() => { setDebouncedQuery(query.trim()); }, 500, [query]);

  const employeesQuery = useInfinityGetEmployees({
    employeeNameOrId: debouncedQuery || '',
  });

  const { fetchNextPage, hasNextPage } = employeesQuery;

  const employees = useMemo(() => {
    const rawEmployees =
      employeesQuery.data?.pages.map(page => page?.employees || []).flat() ||
      [];
    return rawEmployees.filter(Boolean);
  }, [employeesQuery.data]);

  const handleInputChange = useCallback(
    (text: string) => {
      setQuery(text);
      setIsOpen(text.trim().length > 0);
    },
    []
  );

  const handleEmployeeSelect = useCallback(
    (employeeDto: EmployeeDetailsDto) => {
      const employee = mapEmployeeDetails(employeeDto);
      onEmployeeSelect(employee);
      setQuery('');
      setIsOpen(false);
    },
    [onEmployeeSelect]
  );

  const handleClear = useCallback(() => {
    setQuery('');
    setIsOpen(false);
    if (onClear) {
      onClear();
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onClear]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    const c = dropdownRef.current;
    if (!c) return () => { };
    const onScroll = () => {
      if (!hasNextPage || employeesQuery.isFetchingNextPage) return;
      const { scrollTop, scrollHeight, clientHeight } = c;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        fetchNextPage();
      }
    };
    c.addEventListener('scroll', onScroll);
    return () => c.removeEventListener('scroll', onScroll);
  }, [fetchNextPage, hasNextPage, employeesQuery.isFetchingNextPage]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, employeesQuery.data]);

  const isLoading = employeesQuery.isLoading && debouncedQuery.length > 0;
  const hasResults = employees.length > 0;
  const showNoResults = !isLoading && debouncedQuery.length > 0 && !hasResults;

  return (
    <FormField>
      <FieldLabel>
        <T keyName="form.employeeDetails.searchEmployee" />
        <span className="required">*</span>
      </FieldLabel>
      <InputWrapper css={{ position: 'relative' }}>
        <InputIcon
          role={query ? 'button' : undefined}
          onClick={query ? handleClear : undefined}
          css={{
            position: 'absolute',
            top: '50%',
            left: '8px',
            transform: 'translateY(-50%)',
            cursor: query ? 'pointer' : 'default',
            width: '24px',
            height: '24px',
            zIndex: 2,
            pointerEvents: 'auto',
          }}
        >
          {query ? (
            <IoIosClose color="#3F465A" size={24} />
          ) : (
            <SearchIcon />
          )}
        </InputIcon>

        <FormInputWithIcon
          ref={inputRef}
          placeholder={t('form.employeeDetails.searchPlaceholder')}
          value={query}
          onChange={e => handleInputChange(e.target.value)}
          onFocus={() => {
            if (query.trim().length > 0) {
              setIsOpen(true);
            }
          }}
          disabled={isLoading}
          autoComplete="off"
          css={{
            border: '1px solid #CACDD5',
            '&:focus': {
              border: '1px solid #325A6D',
            },
          }}
        />

        {isOpen && (
          <DropdownContainer ref={dropdownRef}>
            {isLoading && (
              <LoadingOption>{t('common.loading')}...</LoadingOption>
            )}

            {hasResults &&
              employees.map(employee => (
                <SelectOption
                  key={employee.employeeGlobalId}
                  onClick={() => handleEmployeeSelect(employee)}
                  role="option"
                  aria-selected={false}
                >
                  <div>
                    <div style={{ fontWeight: 'bold' }}>
                      {employee.employeeName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                      {employee.employeeGlobalId} • {employee.employeeEmail}
                    </div>
                  </div>
                </SelectOption>
              ))}

            {employeesQuery.isFetchingNextPage && (
              <LoadingOption>{t('common.loading')}...</LoadingOption>
            )}

            {showNoResults && (
              <NoResultsOption>{t('common.no_results_found')}</NoResultsOption>
            )}
          </DropdownContainer>
        )}
      </InputWrapper>
    </FormField>
  );
};

